import { useLayoutEffect, useRef, useState } from 'react';

const TRAILING_PUNCT = /[.,;:!?]+$/;
const FALLBACK_CHAR_LIMIT = 110;

/**
 * Build a clean truncated string ending in exactly one ellipsis,
 * cutting at the last word boundary at or before `cutAt`.
 */
export const truncateAtWord = (text, cutAt) => {
  const safeCut = Math.max(0, Math.min(cutAt, text.length));
  const space = text.lastIndexOf(' ', safeCut);
  const cleanCut = space > safeCut * 0.6 ? space : safeCut;
  return text.slice(0, cleanCut).trimEnd().replace(TRAILING_PUNCT, '') + '…';
};

const initialTruncation = (text, charLimit) => {
  if (!text) return { text: '', isLong: false };
  if (text.length <= charLimit) return { text, isLong: false };
  return { text: truncateAtWord(text, charLimit), isLong: true };
};

/**
 * Measure-and-truncate hook for clamping multi-line text to N lines with
 * an inline "Show more" suffix. Lives independently from the surrounding
 * card so multiple consumers (events, community announcements, etc.)
 * can reuse the same DOM-measurement strategy without duplicating ~80
 * lines of layout effect code.
 *
 * Returns:
 *   - ref:           attach to the element rendering the text
 *   - truncatedText: text to render when collapsed
 *   - isLong:        whether the toggle should appear at all
 *
 * Behavior matches the original EventCards implementation:
 *   - SSR / jsdom fallback uses a fixed char limit
 *   - On real DOM, a hidden probe binary-searches the longest substring
 *     whose rendering (+ " Show more") fits within `collapsedLines`
 *   - ResizeObserver re-measures on container width changes
 */
export function useTruncatedText(text, collapsedLines = 3, suffix = ' Show more') {
  const ref = useRef(null);
  const [truncation, setTruncation] = useState(() => initialTruncation(text, FALLBACK_CHAR_LIMIT));

  useLayoutEffect(() => {
    if (!text || !ref.current) return;

    const measure = () => {
      const container = ref.current;
      if (!container) return;

      const cs = window.getComputedStyle(container);
      const width = container.clientWidth;
      if (!width) return;

      const probe = document.createElement('div');
      probe.style.cssText = [
        'position:absolute',
        'left:-9999px',
        'top:0',
        'visibility:hidden',
        `width:${width}px`,
        `font-family:${cs.fontFamily}`,
        `font-size:${cs.fontSize}`,
        `font-weight:${cs.fontWeight}`,
        `line-height:${cs.lineHeight}`,
        `letter-spacing:${cs.letterSpacing}`,
        'white-space:normal',
        'word-wrap:break-word',
      ].join(';');
      document.body.appendChild(probe);

      const lineHeightPx = parseFloat(cs.lineHeight) ||
        parseFloat(cs.fontSize) * 1.55;
      const maxHeight = Math.ceil(lineHeightPx * collapsedLines + 1);

      probe.textContent = text;
      const fullHeight = probe.offsetHeight;

      if (fullHeight <= maxHeight) {
        document.body.removeChild(probe);
        setTruncation({ text, isLong: false });
        return;
      }

      let lo = 1;
      let hi = text.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        probe.textContent = truncateAtWord(text, mid) + suffix;
        if (probe.offsetHeight <= maxHeight) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }

      const finalText = truncateAtWord(text, lo);
      document.body.removeChild(probe);
      setTruncation({ text: finalText, isLong: true });
    };

    measure();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(ref.current);
    }
    return () => {
      if (ro) ro.disconnect();
    };
  }, [text, collapsedLines, suffix]);

  return {
    ref,
    truncatedText: truncation.text,
    isLong: truncation.isLong,
  };
}
